import { defineStore } from 'pinia'

export interface Product {
  id: string
  slug: string
  titles: {
    card: string
    admin: string
    short: string
    long: string
    page: string
    friendly: string
    introHairline: string
  }
  description: {
    short: string
    full: string
  }
  category: 'ethanol' | 'essential-chemicals' | 'manufactured-products'
  categories?: Array<{
    id: number
    name: string
    slug: string
    parent: number
  }>
  origin?: 'natural' | 'synthetic'
  denaturation?: 'denatured' | 'undenatured'
  image: string
  images: string[]
  sizes?: Array<{
    volume: string
    price: number
    unit: string
  }>
  ingredients?: {
    table: Array<{
      ingredient: string
      percentage: string
      casNumber: string
    }>
    list: string[]
  }
  labels?: {
    riskPhrases: string[]
    safetyPhrases: string[]
    warningSigns: string[]
  }
  msds?: string
  hero?: {
    content: {
      title: string
      hairline: string
      description: string
      tags: string[]
    }
    media?: {
      type: 'image' | 'video'
      src: string
      alt: string
    }
  }
  cardcontent?: {
    title: string
    description: string
    hairline: string
  }
  meta?: {
    title: string
    description: string
    ogTitle: string
    ogDescription: string
    ogImage: string
    ogUrl: string
    canonical: string
  }
}

export interface ProductCategory {
  id: number
  slug: string
  name: string
  count: number
  parent?: number
  children?: ProductCategory[]
}

// Lightweight product interface for listing pages
export interface ProductListItem {
  id: string
  slug: string
  title: string
  shortDescription: string
  image: string
  category: string
  categoryName?: string
  categories?: Array<{
    id: number
    name: string
    slug: string
    parent: number
  }>
  origin?: 'natural' | 'synthetic'
  denaturation?: 'denatured' | 'undenatured'
}

export const useProductsStore = defineStore('products', () => {
  const { getProducts, getProduct, getProductCategories } = useWPApi()
  
  // State
  const products = ref<Product[]>([])
  const productList = ref<ProductListItem[]>([]) // Lightweight products for listing
  const categories = ref<ProductCategory[]>([])
  const flatCategories = ref<ProductCategory[]>([]) // Flattened categories for easy access
  const loading = ref(false)
  const error = ref<string | null>(null)
  const isInitialized = ref(false)
  const categoriesInitialized = ref(false)
  const allProductsLoaded = ref(false)
  
  // New state for optimized loading
  const isInitializing = ref(false) // Background initialization in progress
  const categoryProductsLoaded = ref<Record<string, boolean>>({}) // Track which categories are loaded
  const loadedProductIds = ref<Set<string>>(new Set()) // Track loaded product IDs
  
  // Global initialization state
  const isFullyInitialized = ref(false) // True when ALL products and categories are loaded

  // Helper function to convert full product to list item
  const productToListItem = (product: any): ProductListItem => {
    // Use the new clean description fields from API, avoiding undefined strings
    let shortDesc = ''
    
    if (product.description?.short && product.description.short !== 'undefined') {
      shortDesc = product.description.short
    } else if (product.excerpt && product.excerpt !== 'undefined') {
      shortDesc = product.excerpt
    } else if (product.clean_short_description && product.clean_short_description !== 'undefined') {
      shortDesc = product.clean_short_description
    } else if (product.clean_description && product.clean_description !== 'undefined') {
      shortDesc = product.clean_description.substring(0, 150) + '...'
    }
    
    return {
      id: product.id?.toString() || '',
      slug: product.slug,
      title: product.titles?.card || product.name || '',
      shortDescription: shortDesc,
      image: product.image || product.images?.[0] || '',
      category: product.category || product.categories?.[0]?.slug || '',
      categoryName: product.top_category_name || product.categories?.[0]?.name || '',
      categories: product.categories || [],
      origin: product.origin || product.custom_fields?.origin,
      denaturation: product.denaturation || product.custom_fields?.denaturation
    }
  }

  // Helper function to flatten categories
  const flattenCategories = (cats: ProductCategory[], parent?: ProductCategory): ProductCategory[] => {
    let result: ProductCategory[] = []
    for (const cat of cats) {
      const categoryWithParent = { ...cat, parent: parent?.id }
      result.push(categoryWithParent)
      if (cat.children && cat.children.length > 0) {
        result = result.concat(flattenCategories(cat.children, cat))
      }
    }
    return result
  }

  // Fetch categories only
  const fetchCategories = async () => {
    if (categoriesInitialized.value) return
    
    try {
      console.log('Fetching categories...')
      const response = await getProductCategories()
      
      if (response.categories) {
        categories.value = response.categories
        flatCategories.value = flattenCategories(response.categories)
        categoriesInitialized.value = true
        console.log('Categories loaded:', categories.value.length, 'Flat:', flatCategories.value.length)
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch categories'
      console.error('Error fetching categories:', e)
    }
  }

  // Fetch initial products (lightweight, for listing page)
  const fetchInitialProducts = async (page = 1, perPage = 12) => {
    loading.value = true
    error.value = null
    
    try {
      console.log(`Fetching initial products (page ${page}, ${perPage} per page)...`)
      
      // Ensure categories are loaded first
      if (!categoriesInitialized.value) {
        await fetchCategories()
      }
      
      const response = await getProducts({ page, per_page: perPage })
      
      if (response.products) {
        // Convert to lightweight format
        const lightweightProducts = response.products.map(productToListItem)
        
        // If this is the first page, replace the array, otherwise append
        if (page === 1) {
          productList.value = lightweightProducts
          products.value = response.products // Also store full products
        } else {
          productList.value.push(...lightweightProducts)
          products.value.push(...response.products)
        }
        
        isInitialized.value = true
        console.log(`Products loaded: ${productList.value.length} of ${response.pagination.total}`)
        
        // Check if all products are loaded
        if (productList.value.length >= response.pagination.total) {
          allProductsLoaded.value = true
        }
        
        return {
          products: lightweightProducts,
          pagination: response.pagination
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch products'
      console.error('Error fetching products:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  // Fetch all remaining products in background
  const fetchRemainingProducts = async () => {
    if (allProductsLoaded.value) return
    
    try {
      console.log('Fetching remaining products in background...')
      let page = 2
      let hasMore = true
      
      while (hasMore) {
        const response = await getProducts({ page, per_page: 50 })
        
        if (response.products && response.products.length > 0) {
          const lightweightProducts = response.products.map(productToListItem)
          productList.value.push(...lightweightProducts)
          products.value.push(...response.products)
          
          hasMore = productList.value.length < response.pagination.total
          page++
        } else {
          hasMore = false
        }
      }
      
      allProductsLoaded.value = true
      console.log('All products loaded:', productList.value.length)
    } catch (e) {
      console.error('Error fetching remaining products:', e)
    }
  }

  // Fetch products by category (server-side filtering)
  const fetchProductsByCategory = async (categorySlug: string | null, page = 1, perPage = 24) => {
    loading.value = true
    error.value = null
    
    try {
      console.log(`Fetching products for category: ${categorySlug || 'all'}`)
      
      const params: any = { page, per_page: perPage }
      if (categorySlug) {
        params.category = categorySlug
      }
      
      const response = await getProducts(params)
      
      if (response.products) {
        // Convert to lightweight format
        const lightweightProducts = response.products.map(productToListItem)
        
        // Replace the product list with filtered results
        productList.value = lightweightProducts
        products.value = response.products
        
        console.log(`Products loaded for category ${categorySlug}: ${lightweightProducts.length}`)
        
        return {
          products: lightweightProducts,
          pagination: response.pagination
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch products by category'
      console.error('Error fetching products by category:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  // Legacy method for compatibility
  const fetchProducts = async (force = false) => {
    if (isInitialized.value && !force) return
    
    loading.value = true
    error.value = null
    
    try {
      console.log('Fetching products from API...')
      const [productsResponse, categoriesResponse] = await Promise.all([
        getProducts({ per_page: 100 }),
        getProductCategories()
      ])
      
      console.log('Products response:', productsResponse)
      console.log('Categories response:', categoriesResponse)
      
      products.value = productsResponse.products
      productList.value = productsResponse.products.map(productToListItem)
      
      if (categoriesResponse.categories) {
        categories.value = categoriesResponse.categories
        flatCategories.value = flattenCategories(categoriesResponse.categories)
      }
      
      isInitialized.value = true
      categoriesInitialized.value = true
      allProductsLoaded.value = true
      console.log('Products loaded:', products.value.length)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch products'
      console.error('Error fetching products:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchProductBySlug = async (slug: string) => {
    loading.value = true
    error.value = null
    
    try {
      const product = await getProduct(slug)
      
      // Update local store if product exists
      const index = products.value.findIndex(p => p.slug === slug)
      if (index !== -1) {
        products.value[index] = product
      } else {
        products.value.push(product)
      }
      
      return product
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch product'
      console.error('Error fetching product:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  // Computed properties
  const getProductsByCategory = computed(() => (category: string) => {
    if (!category) return products.value
    return products.value.filter(product => product.category === category)
  })

  const getProductBySlug = computed(() => (slug: string) => {
    return products.value.find(product => product.slug === slug)
  })

  const getCategoryBySlug = computed(() => (slug: string) => {
    return categories.value.find(category => category.slug === slug)
  })

  // Filter products (works with lightweight products)
  const filterProducts = (filters: {
    categories?: string[]
    origin?: string[]
    denaturation?: string[]
  }) => {
    let filtered = allProductsLoaded.value ? [...productList.value] : [...productList.value]

    if (filters.categories && filters.categories.length > 0) {
      filtered = filtered.filter(product => filters.categories!.includes(product.category))
    }

    if (filters.origin && filters.origin.length > 0) {
      filtered = filtered.filter(product => 
        product.origin && filters.origin!.includes(product.origin)
      )
    }

    if (filters.denaturation && filters.denaturation.length > 0) {
      filtered = filtered.filter(product => 
        product.denaturation && filters.denaturation!.includes(product.denaturation)
      )
    }

    return filtered
  }

  // Helper function to decode HTML entities
  const decodeHtmlEntities = (text: string): string => {
    if (typeof document === 'undefined') {
      // Server-side fallback
      return text
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&nbsp;/g, ' ')
    }
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text
    return textarea.value
  }

  // Get category options for dropdown (with hierarchy)
  const getCategoryOptions = computed(() => {
    const buildOptions = (cats: ProductCategory[], level = 0): Array<{ label: string; value: string; level: number }> => {
      let options: Array<{ label: string; value: string; level: number }> = []
      
      for (const cat of cats) {
        options.push({
          label: decodeHtmlEntities(cat.name),
          value: cat.slug,
          level
        })
        
        if (cat.children && cat.children.length > 0) {
          options = options.concat(buildOptions(cat.children, level + 1))
        }
      }
      
      return options
    }
    
    return buildOptions(categories.value)
  })

  // Get subcategories for a parent category
  const getCategorySubcategories = (parentSlug: string): ProductCategory[] => {
    const findCategory = (cats: ProductCategory[]): ProductCategory | null => {
      for (const cat of cats) {
        if (cat.slug === parentSlug) return cat
        if (cat.children) {
          const found = findCategory(cat.children)
          if (found) return found
        }
      }
      return null
    }
    
    const parent = findCategory(categories.value)
    return parent?.children || []
  }

  // Fetch products for a specific category and its subcategories
  const fetchCategoryProducts = async (categorySlug: string) => {
    loading.value = true
    error.value = null
    
    try {
      console.log(`Fetching products for category: ${categorySlug}`)
      
      // Mark this category as being loaded
      categoryProductsLoaded.value[categorySlug] = false
      
      const response = await getProducts({ 
        category: categorySlug, 
        per_page: 100 // Get all products for this category
      })
      
      if (response.products) {
        // Convert to lightweight format and add to store
        const lightweightProducts = response.products.map(productToListItem)
        
        // Track loaded product IDs
        lightweightProducts.forEach(p => loadedProductIds.value.add(p.id))
        
        // Merge with existing products (avoid duplicates)
        const existingIds = new Set(productList.value.map(p => p.id))
        const newProducts = lightweightProducts.filter(p => !existingIds.has(p.id))
        
        productList.value.push(...newProducts)
        products.value.push(...response.products.filter(p => !existingIds.has(p.id.toString())))
        
        // Mark category as loaded
        categoryProductsLoaded.value[categorySlug] = true
        isInitialized.value = true
        
        console.log(`Loaded ${newProducts.length} new products for ${categorySlug}`)
        
        return {
          products: lightweightProducts,
          pagination: response.pagination
        }
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch category products'
      console.error('Error fetching category products:', e)
      throw e
    } finally {
      loading.value = false
    }
  }

  // Fetch remaining products excluding already loaded ones
  const fetchRemainingProductsExcluding = async (excludeIds?: Set<string>) => {
    if (allProductsLoaded.value || isInitializing.value) return
    
    isInitializing.value = true
    
    try {
      console.log('Fetching remaining products in background...')
      const idsToExclude = excludeIds || loadedProductIds.value
      
      // For now, we'll fetch all products and filter client-side
      // In future, add server support for exclude parameter
      const response = await getProducts({ per_page: 100 })
      
      if (response.products) {
        const allFetchedProducts = response.products
        const newProducts = allFetchedProducts.filter(p => !idsToExclude.has(p.id.toString()))
        
        if (newProducts.length > 0) {
          const lightweightProducts = newProducts.map(productToListItem)
          
          // Add to store
          productList.value.push(...lightweightProducts)
          products.value.push(...newProducts)
          
          // Update loaded IDs
          newProducts.forEach(p => loadedProductIds.value.add(p.id.toString()))
          
          console.log(`Loaded ${newProducts.length} additional products in background`)
        }
        
        allProductsLoaded.value = true
      }
    } catch (e) {
      console.error('Error fetching remaining products:', e)
    } finally {
      isInitializing.value = false
    }
  }
  
  // Fetch ALL products and categories at once (for global initialization)
  const fetchAllProducts = async () => {
    // Skip if already fully initialized
    if (isFullyInitialized.value) {
      console.log('[Store] Products already fully initialized, skipping fetch')
      return
    }
    
    // Prevent concurrent fetches
    if (loading.value) {
      console.log('[Store] Already loading products, skipping concurrent fetch')
      return
    }
    
    loading.value = true
    error.value = null
    
    try {
      console.log('[Store] Starting to fetch ALL products and categories...')
      
      // Fetch categories first if not already loaded
      if (!categoriesInitialized.value) {
        const categoriesResponse = await getProductCategories()
        if (categoriesResponse.categories) {
          categories.value = categoriesResponse.categories
          flatCategories.value = flattenCategories(categoriesResponse.categories)
          categoriesInitialized.value = true
          console.log(`[Store] Loaded ${categories.value.length} categories`)
        }
      }
      
      // Clear existing data to ensure clean state
      products.value = []
      productList.value = []
      loadedProductIds.value.clear()
      
      // Fetch ALL products with pagination (API limits to 50 per page)
      let page = 1
      let hasMore = true
      let totalFetched = 0
      
      while (hasMore) {
        console.log(`[Store] Fetching products page ${page}...`)
        const response = await getProducts({ per_page: 50, page }) // API max is 50
        
        if (response.products && response.products.length > 0) {
          // Convert to lightweight products
          const lightweightProducts = response.products.map(productToListItem)
          
          // Add products to store
          products.value.push(...response.products)
          productList.value.push(...lightweightProducts)
          
          // Update tracking
          response.products.forEach(p => loadedProductIds.value.add(p.id.toString()))
          
          totalFetched += response.products.length
          console.log(`[Store] Fetched ${response.products.length} products (total: ${totalFetched})`)
          
          // Check if there are more pages
          hasMore = response.pagination && page < response.pagination.total_pages
          page++
        } else {
          hasMore = false
        }
      }
      
      // Mark as fully loaded
      isInitialized.value = true
      allProductsLoaded.value = true
      isFullyInitialized.value = true
      
      console.log(`[Store] Successfully loaded ALL ${products.value.length} products`)
      console.log('[Store] Global product initialization complete!')
      
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch all products'
      console.error('[Store] Error fetching all products:', e)
      // Reset initialization state on error
      isFullyInitialized.value = false
      throw e
    } finally {
      loading.value = false
    }
  }

  // Don't auto-initialize - let pages control when to load
  
  return {
    // State
    products: readonly(products),
    productList: readonly(productList),
    categories: readonly(categories),
    flatCategories: readonly(flatCategories),
    loading: readonly(loading),
    error: readonly(error),
    isInitialized: readonly(isInitialized),
    categoriesInitialized: readonly(categoriesInitialized),
    allProductsLoaded: readonly(allProductsLoaded),
    isInitializing: readonly(isInitializing),
    categoryProductsLoaded: readonly(categoryProductsLoaded),
    isFullyInitialized: readonly(isFullyInitialized),
    
    // Actions
    fetchProducts,
    fetchCategories,
    fetchInitialProducts,
    fetchRemainingProducts,
    fetchProductsByCategory,
    fetchProductBySlug,
    fetchCategoryProducts,
    fetchRemainingProductsExcluding,
    fetchAllProducts,
    filterProducts,
    
    // Getters
    getProductsByCategory,
    getProductBySlug,
    getCategoryBySlug,
    getCategoryOptions,
    getCategorySubcategories
  }
})